import React, { useState, useRef, useEffect } from 'react';
import { Plus, ChevronRight, Trash2, Pencil, Gift, ListChecks } from 'lucide-react';

/**
 * FunnelStepBar — horizontal step flow bar for funnel-type sites.
 * Shows ordered funnel steps with drag-to-reorder, status dots,
 * and a context menu for step management.
 */
export default function FunnelStepBar({
  pages = [],
  currentPageId,
  onSelectPage,
  onAddStep,
  onReorderSteps,
  onDeleteStep,
  onUpdateStep,
  onSetFormFlow,
}) {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { pageId, x, y }
  const barRef = useRef(null);
  const contextRef = useRef(null);

  // Close context menu on outside click
  useEffect(() => {
    function handleClick(e) {
      if (contextRef.current && !contextRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    }
    if (contextMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [contextMenu]);

  const sorted = [...pages].sort(
    (a, b) => (a.funnel_order ?? 0) - (b.funnel_order ?? 0)
  );

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    if (dragIndex != null && dragIndex !== toIndex) {
      onReorderSteps(dragIndex, toIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleContextMenu = (e, pageId) => {
    e.preventDefault();
    setContextMenu({ pageId, x: e.clientX, y: e.clientY });
  };

  const getStepTypeLabel = (page) => {
    if (page.page_type === 'thank_you') return 'Thank You';
    if (page.page_type === 'upsell') return 'Upsell';
    if (page.page_type === 'form_flow') return 'Form Flow';
    return null;
  };

  const getStatusColor = (page) => {
    if (page.published_version) return 'bg-emerald-400';
    return 'bg-amber-400';
  };

  return (
    <div
      ref={barRef}
      className="h-12 bg-white border-b border-stone-200 flex items-center px-4 gap-1 overflow-x-auto flex-shrink-0"
      style={{ scrollbarWidth: 'thin' }}
    >
      {sorted.map((page, i) => {
        const isActive = page.page_id === currentPageId;
        const isDragging = dragIndex === i;
        const isDragOver = dragOverIndex === i;
        const typeLabel = getStepTypeLabel(page);

        return (
          <React.Fragment key={page.page_id}>
            {/* Connector arrow (not before first) */}
            {i > 0 && (
              <ChevronRight className="w-3.5 h-3.5 text-stone-300 flex-shrink-0 mx-0.5" />
            )}

            {/* Step pill */}
            <button
              draggable
              onDragStart={(e) => handleDragStart(e, i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDragEnd={handleDragEnd}
              onDrop={(e) => handleDrop(e, i)}
              onClick={() => onSelectPage(page.page_id)}
              onContextMenu={(e) => handleContextMenu(e, page.page_id)}
              className={`
                flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-medium
                whitespace-nowrap flex-shrink-0 transition-all cursor-grab active:cursor-grabbing
                ${isActive
                  ? 'bg-blue-50 text-blue-700 ring-2 ring-blue-500 ring-offset-1'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }
                ${isDragging ? 'opacity-40' : ''}
                ${isDragOver && dragIndex !== i ? 'ring-2 ring-blue-300' : ''}
              `}
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {/* Status dot */}
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(page)}`} />

              {/* Step label */}
              <span className="text-[10px] text-stone-400 font-semibold">
                {i + 1}.
              </span>
              <span className="truncate max-w-[120px]">{page.name}</span>

              {/* Type badge if special */}
              {typeLabel && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-stone-200 text-stone-500 uppercase tracking-wide">
                  {typeLabel}
                </span>
              )}
            </button>
          </React.Fragment>
        );
      })}

      {/* Add step button */}
      <button
        onClick={onAddStep}
        className="w-8 h-8 rounded-full border-2 border-dashed border-stone-300 flex items-center justify-center
          text-stone-400 hover:text-stone-600 hover:border-stone-400 transition-colors flex-shrink-0 ml-1"
        title="Add funnel step"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>

      {/* Context menu */}
      {contextMenu && (
        <div
          ref={contextRef}
          className="fixed bg-white rounded-xl border border-stone-200 shadow-lg z-50 py-1 w-48"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
            onClick={() => {
              const page = pages.find(p => p.page_id === contextMenu.pageId);
              const newName = window.prompt('Rename step:', page?.name || '');
              if (newName && newName.trim()) {
                onUpdateStep(contextMenu.pageId, { name: newName.trim() });
              }
              setContextMenu(null);
            }}
          >
            <Pencil className="w-4 h-4 text-stone-400" />
            Rename
          </button>
          <button
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
            onClick={() => {
              onUpdateStep(contextMenu.pageId, { page_type: 'thank_you' });
              setContextMenu(null);
            }}
          >
            <Gift className="w-4 h-4 text-stone-400" />
            Set as Thank You Page
          </button>
          <button
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
            onClick={() => { if (onSetFormFlow) onSetFormFlow(contextMenu.pageId); setContextMenu(null); }}
          >
            <ListChecks className="w-4 h-4 text-stone-400" />
            Set as Form Flow
          </button>
          <button
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
            onClick={() => {
              if (pages.length <= 1) {
                return; // Don't delete the last step
              }
              onDeleteStep(contextMenu.pageId);
              setContextMenu(null);
            }}
          >
            <Trash2 className="w-4 h-4" />
            Delete Step
          </button>
        </div>
      )}
    </div>
  );
}
